import { FormEvent, useEffect, useState } from "react"
import { useEmpresa } from "../../app/context/EmpresaContext"
import { apiFetch } from "../../lib/api"
import { getAccessToken, redirectToLoginOnUnauthorized } from "../../lib/auth"
import { config } from "../../lib/config"

type Cert = { slot: string; validade: string | null; subject_cn: string | null }

type CertSlot = "hom" | "prod"

const SLOT_TITLE: Record<CertSlot, string> = {
  hom: "Homologação",
  prod: "Produção",
}

const SLOT_HINT: Record<CertSlot, string> = {
  hom: "Testes na SEFAZ (ambiente 2)",
  prod: "NF-e com validade jurídica (ambiente 1)",
}

export const CertificadosPage = () => {
  const { empresaId, empresas } = useEmpresa()
  const empresa = empresas.find((e) => e.id === empresaId)
  const [certs, setCerts] = useState<Cert[]>([])
  const [slot, setSlot] = useState<CertSlot>("hom")
  const [aplicarAmbos, setAplicarAmbos] = useState(false)
  const [message, setMessage] = useState("")
  const [isError, setIsError] = useState(false)
  const [uploading, setUploading] = useState(false)

  const load = async () => {
    if (!empresaId) return
    const data = await apiFetch<Cert[]>(`/v1/empresas/${empresaId}/certificados`)
    setCerts(data)
  }

  useEffect(() => {
    load()
  }, [empresaId])

  const handleUpload = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!empresaId) {
      setIsError(true)
      setMessage("Selecione uma empresa no menu lateral (Empresa ativa).")
      return
    }
    const form = e.currentTarget
    const fd = new FormData(form)
    setMessage("")
    setIsError(false)
    setUploading(true)
    try {
      const token = getAccessToken()
      const destino = aplicarAmbos ? "ambos" : slot
      const res = await fetch(`${config.apiUrl}/v1/empresas/${empresaId}/certificados/${destino}`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      })
      if (res.status === 401) {
        redirectToLoginOnUnauthorized()
        return
      }
      if (!res.ok) throw new Error(await res.text())
      setMessage(
        aplicarAmbos
          ? "Certificado enviado para homologação e produção."
          : `Certificado de ${SLOT_TITLE[slot]} enviado com sucesso.`,
      )
      form.reset()
      await load()
    } catch (err) {
      setIsError(true)
      setMessage(err instanceof Error ? err.message : "Falha no upload")
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Certificados A1</h1>
        <p className="mt-1 text-sm text-slate-600">
          O certificado é vinculado à <strong>empresa ativa</strong> no menu lateral. Cada CNPJ pode ter um arquivo para homologação e outro para produção.
        </p>
      </div>

      {!empresaId ? (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Nenhuma empresa selecionada. Use o seletor <strong>Empresa ativa</strong> na barra lateral.
        </p>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800">
          <span className="font-medium">Empresa ativa:</span>{" "}
          {empresa?.nome_fantasia || empresa?.razao_social || empresaId}
          {empresa?.cnpj ? <span className="text-slate-600"> — CNPJ {empresa.cnpj}</span> : null}
        </div>
      )}

      <form onSubmit={handleUpload} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
        <h2 className="font-semibold text-slate-900">Enviar certificado (.pfx / .p12)</h2>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-slate-700">Ambiente</legend>
          <div className={`flex flex-col gap-2 sm:flex-row sm:gap-6 ${aplicarAmbos ? "opacity-50" : ""}`}>
            {(Object.keys(SLOT_TITLE) as CertSlot[]).map((value) => (
              <label key={value} className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="slot_ui"
                  value={value}
                  checked={slot === value}
                  onChange={() => setSlot(value)}
                  className="mt-1"
                  disabled={aplicarAmbos}
                />
                <span>
                  <span className="font-medium">{SLOT_TITLE[value]}</span>
                  <span className="block text-slate-600">{SLOT_HINT[value]}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
          <input
            type="checkbox"
            checked={aplicarAmbos}
            onChange={(e) => setAplicarAmbos(e.target.checked)}
            className="mt-1"
            aria-label="Usar o mesmo arquivo em homologação e produção"
          />
          <span>
            <span className="font-medium">Usar o mesmo arquivo em homologação e produção</span>
            <span className="block text-slate-600">Grava este .pfx e esta senha nos dois ambientes de uma vez.</span>
          </span>
        </label>

        <p className="text-xs text-slate-500">
          Em homologação a NF-e é de teste (ambiente 2). Em produção a nota tem validade jurídica.
          Se os ambientes usam certificados diferentes, desmarque a opção e envie cada arquivo no ambiente correspondente.
        </p>

        <div className="space-y-2">
          <label htmlFor="cert-arquivo" className="text-sm font-medium text-slate-700">
            Arquivo do certificado
          </label>
          <input id="cert-arquivo" name="arquivo" type="file" accept=".pfx,.p12" required className="block w-full text-sm" />
        </div>

        <div className="space-y-2">
          <label htmlFor="cert-senha" className="text-sm font-medium text-slate-700">
            Senha do certificado
          </label>
          <input
            id="cert-senha"
            name="senha"
            type="password"
            autoComplete="off"
            placeholder="Senha definida ao exportar o A1"
            required
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
        </div>

        <button
          type="submit"
          disabled={uploading || !empresaId}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
        >
          {uploading ? "Enviando…" : aplicarAmbos ? "Enviar para homologação e produção" : `Enviar para ${slot === "hom" ? "homologação" : "produção"}`}
        </button>
      </form>

      {message && (
        <p className={`text-sm ${isError ? "text-red-700" : "text-emerald-700"}`}>{message}</p>
      )}

      <div>
        <h2 className="mb-2 font-semibold text-slate-900">Certificados cadastrados nesta empresa</h2>
        <ul className="rounded-xl border border-slate-200 bg-white divide-y">
          {certs.length === 0 ? (
            <li className="px-5 py-3 text-sm text-slate-500">Nenhum certificado ainda.</li>
          ) : (
            certs.map((c) => (
              <li key={c.slot} className="px-5 py-3 text-sm">
                <span className="font-medium uppercase">{c.slot === "hom" ? "Homologação" : c.slot === "prod" ? "Produção" : c.slot}</span>
                {" — "}
                {c.subject_cn ?? "—"}
                {" — validade "}
                {c.validade ? new Date(c.validade).toLocaleDateString("pt-BR") : "—"}
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  )
}
